import { useCallback } from 'react'
import log from 'loglevel'
import { VendorQuestionnaireReceivedServicePromiseClient } from '@trustero/trustero-api-web/lib/tprmquestionnaire/vendor_questionnaire_received_grpc_web_pb'
import {
  DeleteReceivedVendorQuestionnaireRequest,
  ListReceivedVendorQuestionnairesRequest,
  ListReceivedVendorQuestionnairesResponse,
} from '@trustero/trustero-api-web/lib/tprmquestionnaire/vendor_questionnaire_received_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useConfirmationContext } from 'src/Confirmation'
import { useThrobberContext } from 'src/Throbber/ThrobberContext'
import { showInfoToast } from 'src/Utils/helpers/toast'

// Data-fetching hooks shared across the Vendor Questionnaires tab's
// index/show/answer pages (and the Versions tab's preview modal) -- the
// JWT-authenticated listing hook. The vendor portal used to carry its own
// copy of all of this (VendorPortalQuestionnaire.hooks.tsx, now deleted); both
// surfaces read through components/Reusable/Questionnaire instead.
// Reads go through SWR (see src/pages/Vendors/vendors.hooks.tsx for the
// convention this follows) since this tab is reached by a regular,
// JWT-authenticated account rather than a vendor's session-cookie flow.
// Hooks used by only one page's own components (e.g. the Versions tab's
// useReceivedVendorQuestionnaireVersions, or the answer page's
// save/submit/comment mutations) live with that page instead of here --
// see show/VendorQuestionnaireShowPage.hooks.tsx and
// src/components/Reusable/Questionnaire/questionnaireAnswer.hooks.tsx.
//
// Response shapes returned by VendorQuestionnaireReceivedServicePromiseClient
// mirror the vendor portal's vendorportal.proto messages field-for-field
// (with a "Received" prefix and no session_token field), so the same
// `.toObject()` conversion the vendor portal hooks already rely on produces
// values structurally compatible with the shared, protobuf-agnostic
// QuestionnaireSection/QuestionnaireQuestion/QuestionnaireOption/
// QuestionnaireCommentItem types from src/components/Reusable/Questionnaire
// -- see QuestionnaireTypes.ts. No separate mapping functions are needed.

// useReceivedVendorQuestionnaires fetches the index tab's data. Also used by
// the show page to derive its prev/next pager order -- see
// VendorQuestionnaireShowPage.hooks.tsx's doc comment on why that pager
// ignores the index tab's own filter/sort state.
export const useReceivedVendorQuestionnaires = (
  shouldFetch = true,
): GrpcResponse<ListReceivedVendorQuestionnairesResponse> => {
  const { response } = useSwrImmutableGrpc(
    VendorQuestionnaireReceivedServicePromiseClient.prototype
      .listReceivedVendorQuestionnaires,
    new ListReceivedVendorQuestionnairesRequest(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// useDeleteReceivedVendorQuestionnaireWithConfirmation asks, then soft-deletes
// a questionnaire this account imported (DeleteReceivedVendorQuestionnaire) and
// calls onDeleted to refresh the list. Nothing is destroyed: the questionnaire
// leaves the list and Trustero Intelligence stops using its answers.
export const useDeleteReceivedVendorQuestionnaireWithConfirmation = (
  onDeleted: () => Promise<unknown> | void,
): ((requestId: string, name: string) => void) => {
  const client = useAuthorizedGrpcClient(
    VendorQuestionnaireReceivedServicePromiseClient,
  )
  const { setConfirmationState } = useConfirmationContext()
  const { startThrobber, stopThrobber } = useThrobberContext()

  return useCallback(
    (requestId: string, name: string) => {
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: `Delete ${name}?`,
        body: "It's removed from this list, and Trustero Intelligence no longer uses its answers.",
        confirmText: 'Delete Questionnaire',
        onConfirmCB: async () => {
          try {
            startThrobber()
            await client.deleteReceivedVendorQuestionnaire(
              new DeleteReceivedVendorQuestionnaireRequest().setRequestId(
                requestId,
              ),
            )
            await onDeleted()
          } catch (err) {
            log.error('Error deleting received questionnaire', err)
            showInfoToast(
              'There was an error deleting this questionnaire. Please try again.',
            )
          } finally {
            stopThrobber()
          }
        },
      })
    },
    [client, setConfirmationState, startThrobber, stopThrobber, onDeleted],
  )
}
